// Per-element force-breakdown arrows over the live foil hero (FXC-9009 Phase 3).
//
// An additive SVG overlay anchored in the SAME chord-coordinate `view` (ViewRect)
// the foil canvas + LIC field share, so an arrow drawn at an element's
// centre-of-pressure maps through the identical letterbox world→pixel transform
// the geometry handles use. One arrow per element, direction from (CFx, CFz),
// length scaled to |force|; the tooltip states the hinge tendency (STOW/DEPLOY)
// computed ONCE in hinge.ts rather than re-derived here.
//
// HONEST-ABSENT: renders nothing when no per-element forces are present. The loads
// arrive from either the run-table per-element scalars (element_<name>_CFx/…) or an
// `elementForces` array on the /flow FlowMesh dict — neither exists until the remote
// rans.pipeline runner emits them.
// TODO(highlift-studio): lights up when the rans runner emits per-element forces.

import { useMemo } from "react";
import { letterbox, type ViewRect } from "../geometry";
import { hingePhrase, type ElemLoad } from "../geometry/hinge";
import { useHighliftTheme } from "./useHighliftTheme";

/** One element's load + where it acts (chord coords). */
export interface ElementForceArrow extends ElemLoad {
  element: string;
  /** Centre-of-pressure in chord coords (x aft, z up). Defaults to (0.25, 0). */
  xCp?: number;
  yCp?: number;
}

export interface HighliftForceArrowsProps {
  forces: ElementForceArrow[] | null | undefined;
  view: ViewRect;
  width: number;
  height: number;
}

/** Element name → themed stroke colour (matches the --el-* palette). */
function elementColor(name: string): string {
  const key = name.toLowerCase();
  if (key.includes("slat")) return "var(--el-slat)";
  if (key.includes("vane")) return "var(--el-vane)";
  if (key.includes("flap") || key.includes("flaperon")) return "var(--el-flap)";
  return "var(--el-main)";
}

export function HighliftForceArrows({ forces, view, width, height }: HighliftForceArrowsProps) {
  const palette = useHighliftTheme();
  const arrows = useMemo(() => {
    if (!forces?.length || width <= 0 || height <= 0) return null;
    const { s, ox, oy } = letterbox(view, width, height);
    // world → pixel (y-flip, matching FoilGeometryCanvas world()).
    const toPx = (x: number, z: number): [number, number] => [
      ox + s * (x - view.xmin),
      height - (oy + s * (z - view.ymin)),
    ];
    // Scale |force| → pixels so the largest arrow is a readable fraction of the
    // stage. Coefficients are O(0.1–2); pick a px-per-unit so the max maps to ~70px.
    const maxMag = Math.max(
      1e-6,
      ...forces.map((f) => Math.hypot(f.CFx, f.CFz)),
    );
    const pxPerUnit = 70 / maxMag;
    return forces
      .map((f) => {
        const mag = Math.hypot(f.CFx, f.CFz);
        if (!Number.isFinite(mag) || mag < 1e-9) return null;
        const xCp = Number.isFinite(f.xCp) ? (f.xCp as number) : 0.25;
        const yCp = Number.isFinite(f.yCp) ? (f.yCp as number) : 0;
        const [x0, y0] = toPx(xCp, yCp);
        // Force direction in pixels: +CFx aft (+x screen), +CFz up (−y screen).
        const dx = f.CFx * pxPerUnit;
        const dy = -f.CFz * pxPerUnit;
        const x1 = x0 + dx;
        const y1 = y0 + dy;
        // Arrowhead.
        const ang = Math.atan2(y1 - y0, x1 - x0);
        const hl = 7;
        const ha = 0.45;
        return {
          element: f.element,
          color: elementColor(f.element),
          x0, y0, x1, y1,
          head: [
            [x1, y1],
            [x1 - hl * Math.cos(ang - ha), y1 - hl * Math.sin(ang - ha)],
            [x1 - hl * Math.cos(ang + ha), y1 - hl * Math.sin(ang + ha)],
          ] as Array<[number, number]>,
          title: `${f.element}: CFx ${f.CFx.toFixed(3)} · CFz ${f.CFz.toFixed(3)} · CMy ${f.CMy.toFixed(3)} — ${hingePhrase(f.CMy)}`,
        };
      })
      .filter((a): a is NonNullable<typeof a> => a != null);
  }, [forces, view, width, height]);

  if (!arrows || arrows.length === 0) return null;
  return (
    <svg
      width={width}
      height={height}
      style={{ position: "absolute", inset: 0, zIndex: 2, pointerEvents: "none" }}
      aria-label="per-element force breakdown"
    >
      {arrows.map((a) => (
        <g key={a.element}>
          <title>{a.title}</title>
          <line x1={a.x0} y1={a.y0} x2={a.x1} y2={a.y1} stroke={a.color} strokeWidth={2} opacity={0.9} />
          <polygon points={a.head.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(" ")} fill={a.color} opacity={0.9} />
          <circle cx={a.x0} cy={a.y0} r={2.5} fill={palette.surface} stroke={a.color} strokeWidth={1.4} />
        </g>
      ))}
    </svg>
  );
}
